import { GRAMMAR } from '@src/components/Cards/constants'
import {
  checkInitialCategory,
  hardRedirect,
  HardRedirectPayload,
  promptLoginWithRedirect,
  scrollSuggestion,
  ScrollSuggestionPayload,
  setHasMaxFreeComments,
  setInitialCategory,
  switchLocale,
  SwitchLocalePayload,
} from '@src/state/workspace/actions'
import { Check, CheckWithMetadata } from '@src/types/checks'
import { clearWfeLS, getWFELS, setWFELS } from '@src/utils/localStorage'
import { parseQueryParams } from '@src/utils/queryParams'
import { push } from 'connected-react-router'
import { convertFromRaw, EditorState } from 'draft-js'
import { createLogic } from 'redux-logic'
import { getType } from 'typesafe-actions'
import { setParsedJwt, setSuccessRedirect, showCheggAuthModal } from '../auth/actions'
import { getPaidMember } from '../auth/selectors'
import { WritingToolsJWT } from '../auth/types'
import { runChecks, setCheck } from '../checks/actions'
import { CheckState } from '../checks/reducer'
import { allChecksComplete } from '../checks/selectors'
import {
  DocumentInfo,
  EditorStatePayload,
  loadModules,
  setActiveCommentId,
  setComments,
  setEditorState,
  setPlagiarismScore,
} from '../document/actions'
import { BLOCK_KEY } from '../document/document.helper'
import { isCheckCategory } from '../document/helpers'
import { migrateComments, migrateEditorState } from '../document/helpers/draftjs.helper'
import {
  selectCommentsWithCustomEntities,
  selectInteractableComments,
} from '../document/selectors'
import { getPathWithSearch, isDemoSelector } from '../router/selectors'
import {
  checkFromLocalStorage,
  CheckFromLocalStoragePayload,
  clearLocalStorage,
  loadFromLocalStorage,
  persistToLocalStorage,
} from '../session/actions'
import { getStateToPersist } from '../session/selectors'
import { WfeLS } from '../session/types'
import { RootState } from '../types'
import { selectUpdateHaxMaxFreeComments } from './selectors'

/**
 *
 * The `promptLoginWithRedirect` action is called when a guest user
 * attempts to visit the editor workspace, and does not have any
 * paper content in localstorage.
 *
 * This workflow assumes a user is visiting WFE for the first time on a new device.
 * We set the protected path they intend to visit in state, then navigate to the homepage and prompt
 * the user to log in. Upon log in success, we navigate them back to the resource they first
 * intended to visit.
 *
 * See the tsdoc comment in `src/state/auth/types.ts` on `successRedirect` for more.
 *
 */
export const promptLoginWithRedirectLogic = createLogic<RootState>({
  type: getType(promptLoginWithRedirect),
  process({ getState }, dispatch, done) {
    dispatch(setSuccessRedirect({ successRedirect: getPathWithSearch(getState()) }))
    dispatch(push('/'))
    dispatch(showCheggAuthModal({ experience: 'login' }))
    done()
  },
})

export const scrollSuggestionLogic = createLogic<RootState, ScrollSuggestionPayload>({
  type: getType(scrollSuggestion),
  process({ getState, action }, dispatch, done) {
    const state = getState()
    const { increment } = action.payload as ScrollSuggestionPayload

    const currentIds: string[] = selectCommentsWithCustomEntities(state).map(c => c.id)

    // Currently if we have not interacted with any entities, clicking "next" or
    // "prev" will not show the next/prev card, since `lastTouchedEntityId` is null
    const lastTouchedCommentId = state.workspace.lastTouchedEntityId || currentIds[0]

    const index = currentIds.findIndex(id => id === lastTouchedCommentId)

    let newIndex = index + increment

    if (!currentIds[newIndex]) {
      newIndex = increment > 0 ? 0 : currentIds.length - 1
    }

    const newId = currentIds[newIndex]
    dispatch(setActiveCommentId({ id: newId, component: 'mobile' }))

    done()
  },
})

export const setHasMaxFreeCommentsLogic = createLogic<RootState, EditorStatePayload>({
  type: getType(setEditorState),
  validate({ getState, action }, allow, reject) {
    const state = getState()
    const isPaid = getPaidMember(state)
    const isDemo = isDemoSelector(state)
    const { hasMaxFreeComments } = state.workspace

    if (isPaid || isDemo || hasMaxFreeComments === true) {
      return reject(action)
    }

    const comments = selectInteractableComments(state)

    /* at this point there will always only be 19 cards in the 
    editorState, which is because the blurred card is the 20th(?)*/
    const count = comments.length + 1

    const hasMaxFree = selectUpdateHaxMaxFreeComments(count)(state)

    if (hasMaxFree) {
      allow(action)
    } else {
      reject(action)
    }
  },
  process({}, dispatch, done) {
    dispatch(setHasMaxFreeComments({ value: true }))
    done()
  },
})

export const hardRedirectLogic = createLogic<RootState, HardRedirectPayload>({
  type: getType(hardRedirect),
  process({ action }, _, done) {
    const { url } = action.payload as HardRedirectPayload
    window.open(url, '_blank')
    done()
  },
})

export const handleFreshTokenLogic = createLogic<RootState, WritingToolsJWT>({
  type: getType(setParsedJwt),
  validate({ getState, action }, allow, reject) {
    const { paidMember } = action.payload as WritingToolsJWT
    const isDemo = isDemoSelector(getState())
    if (paidMember || isDemo) {
      allow(action)
    } else {
      reject(action)
    }
  },
  process(_, dispatch, done) {
    dispatch(setHasMaxFreeComments({ value: false }))
    done()
  },
})

// TODO: WTP-1668 - remove logic, call utils directly
export const persistToLocalStorageLogic = createLogic<RootState>({
  type: getType(persistToLocalStorage),
  process({ getState }, _, done) {
    const state = getState()
    const wfeLs = getStateToPersist(state)
    setWFELS(wfeLs)
    done()
  },
})

// TODO: WTP-1668 - remove logic, call utils directly
export const clearLocalStorageLogic = createLogic<RootState>({
  type: getType(clearLocalStorage),
  process({}, _, done) {
    clearWfeLS()
    done()
  },
})

export const getCheckCounts = (checkState: CheckState) =>
  Object.keys(checkState).reduce<number>(
    (count: number, checkType: string) => count + (checkState[checkType].count || 0),
    0
  )

export const checkCountLogic = createLogic<RootState, CheckWithMetadata>({
  type: getType(setCheck),
  transform({ getState, action }, next) {
    const { metadata, ...payloadWithoutMeta } = action.payload as CheckWithMetadata
    next({
      type: action.type,
      payload: { ...payloadWithoutMeta, count: metadata.length },
    })
  },
})

export const handleSetCheckLogic = createLogic<RootState, Check>({
  type: getType(setCheck),
  validate({ getState, action }, allow, reject) {
    const state = getState()
    const check = action.payload
    // reject if all checks are not complete
    const mergedCheckState = {
      ...state.checks,
      [check.type]: {
        finished: true,
        count: check.count,
        type: check.type,
      },
    }

    if (!allChecksComplete(mergedCheckState, state.workspace.checksToExpect)) {
      reject(action)
    } else {
      allow(action)
    }
  },
  process({ getState, action }, dispatch, done) {
    const state = getState()
    const check = action.payload as Check
    // reject if all checks are not complete
    const mergedCheckState = {
      ...state.checks,
      check,
    }
    const { maxFreeComments } = state.workspace
    if (getCheckCounts(mergedCheckState) > maxFreeComments) {
      dispatch(setHasMaxFreeComments({ value: true }))
    }
    done()
  },
})

export const checkFromLocalStorageLogic = createLogic<
  RootState,
  CheckFromLocalStoragePayload
>({
  type: getType(checkFromLocalStorage),
  validate({ action }, allow, reject) {
    const wfeLs = getWFELS()
    const { id } = action.payload as DocumentInfo
    if (!wfeLs || wfeLs.uploadId !== id) {
      reject(push('/notfound'))
    } else {
      allow({
        type: action.type,
        payload: {
          ...wfeLs,
        },
      })
    }
  },
  process({ action }, dispatch, done) {
    const wfeLs = action.payload as WfeLS
    dispatch(loadFromLocalStorage({ ...wfeLs }))
    done()
  },
})

export const loadFromLocalStorageLogic = createLogic<RootState, WfeLS>({
  type: getType(loadFromLocalStorage),
  process({ action }, dispatch, done) {
    const { rawContentState, comments, plagiarismScore, version } = action.payload
    if (version === BLOCK_KEY) {
      const newEditorState = migrateEditorState({
        editorState: EditorState.createWithContent(convertFromRaw(rawContentState)),
        blockVersion: BLOCK_KEY,
      })
      dispatch(setEditorState({ editorState: newEditorState }))
      dispatch(loadModules({ editorState: newEditorState }))
      // TODO: WTP-1695 to unify loading from persistence layer
      const newComments = migrateComments(newEditorState.getCurrentContent(), comments)
      dispatch(setComments(newComments))
      if (typeof plagiarismScore === 'number') {
        dispatch(setPlagiarismScore({ score: plagiarismScore }))
      }
    }
    done()
  },
})

export const switchLocaleLogic = createLogic<RootState, SwitchLocalePayload>({
  type: getType(switchLocale),
  process(_, dispatch, done) {
    dispatch(runChecks({ isRecheck: true, checkCategory: GRAMMAR }, {}))
    done()
  },
})

export const checkInitialCategoryLogic = createLogic<RootState>({
  type: getType(checkInitialCategory),
  process({ getState }, dispatch, done) {
    const { router } = getState()
    const { checkCategory } = parseQueryParams(router.location.search)
    if (isCheckCategory(checkCategory)) {
      dispatch(setInitialCategory({ checkCategory }))
    }

    done()
  },
})

export default [
  scrollSuggestionLogic,
  hardRedirectLogic,
  handleFreshTokenLogic,
  setHasMaxFreeCommentsLogic,
  persistToLocalStorageLogic,
  handleSetCheckLogic,
  loadFromLocalStorageLogic,
  clearLocalStorageLogic,
  checkFromLocalStorageLogic,
  checkCountLogic,
  switchLocaleLogic,
  checkInitialCategoryLogic,
  promptLoginWithRedirectLogic,
]



// WEBPACK FOOTER //
// ./src/state/workspace/logic.ts